import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import moment from "moment";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Sentinel strings the API has historically used on date fields to mean "no
// value". The dashboard now sends null instead, but older backend paths (and
// other clients) may still send these, so keep tolerating them.
const MISSING_DATE_SENTINELS = ["", "n/a", "na", "null", "undefined", "unknown", "error"];

/**
 * True when a value from the API is a real, parseable date.
 *
 * Guards two traps that produce plausible-looking garbage rather than an error:
 *   new Date(null)          -> the 1970 epoch, which formats as "12-31-1969"
 *   moment(new Date("N/A")) -> the literal string "Invalid date"
 * Both have shipped to customers before; check with this before formatting.
 */
export const isValidApiDate = (date: any): boolean => {
  if (date === null || date === undefined) return false;
  // Bare numbers are never dates in these payloads, and Date() guesses a year
  // from them rather than failing: new Date("0") -> 2000-01-01, new Date("2025")
  // -> 2025-01-01. A MySQL zero-date or an integer default would sail through.
  // (Unix timestamps go through unixTimeToDate, not here.)
  if (typeof date === "number") return false;
  const raw = String(date).trim();
  if (MISSING_DATE_SENTINELS.includes(raw.toLowerCase())) return false;
  if (/^\d+$/.test(raw)) return false;
  return moment(new Date(raw)).isValid();
};

/**
 * Format an API date for display. Returns `fallback` for null, empty, sentinel
 * and unparseable values instead of rendering "Invalid date" or "12-31-1969".
 */
export const dateFormat = (
  date: any,
  useDash: boolean = false,
  fallback: string = "N/A"
) => {
  if (!isValidApiDate(date)) return fallback;
  const format = useDash ? "MM-DD-YYYY" : "MM/DD/YYYY";
  return moment(new Date(date)).format(format);
};

export const unixTimeToDate = (timestamp: any) => {
  const date = new Date(timestamp * 1000);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  const formattedDate = `${month}-${day}-${year}`;
  return formattedDate;
}

export const currencyFormat = (number: number) => {
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const phoneFormat = (input: any) => {
  if (input) {
    input = input.replace(/\D/g, "").substring(0, 10);
    var size = input.length;
    if (size > 0) {
      input = "(" + input;
    }
    if (size > 3) {
      input = input.slice(0, 4) + ") " + input.slice(4);
    }
    if (size > 6) {
      input = input.slice(0, 9) + "-" + input.slice(9);
    }
  }
  return input;
};

export const formatDate = (dateString: string): string => {
  return dateString.split("T")[0];
};

export const skipUploadStipArray: string[] = [
  "Bank VOD",
  "Merchant Interview",
  "Signed Contracts",
  "Signed Cross Corporate Guarantee",
  "Signed Renewal Addendum",
  "Signed Third Party Authorization Letter",
  "ISO\'s California License Number",
  "Funding Call",
  "Verify SSN Prior to Contracts Out",
  "Verify EIN # Prior to Contracts Out",
];

export const mandatoryUploadStipArray: string[] = [
  "Voided Check",
];

// ENG-4547: only an AM status of exactly "Performing" is eligible for the
// renewal request flow (Apply Now / View Offer) or the A/B (is_even) flow.
// Anything else (including "Not Performing", "Monitor Payments", missing/null,
// etc.) is treated as not performing per product direction.
export const isNotPerformingAmStatus = (amstatus?: string | null): boolean =>
  (amstatus ?? "").trim().toLowerCase() !== "performing";

export const trimString = (s: string, max = 60) => {
  if (!s) return "";
  return s.length > max ? s.slice(0, max - 3) + "..." : s;
};

export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const parts = result.split(",");
      resolve(parts[1] || "");
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};


export const formatingPaymentType = (key?: string) => {
  if (!key) return "";
  let value = "week";
  if (key === "daily") {
    return "day";
  }
  return value;
};


export const mcaPathForStep = (n: number, status?: number) => {
  console.log(n, status, "mcaPathForStep")
  switch (true) {
    case n === 1:
      return "/online-checkout/view-offer";
    case n === 2 && status === 1:
      return "/online-checkout/bank-confirmation";
    case n === 2 && status === 2:
      return "/online-checkout/bank-verification";
    case n === 2 && status === 3:
      return "/online-checkout/bank-select-confirmation";
    case n === 3:
      return "/online-checkout/identity-verification";
    case n === 4:
      return "/online-checkout/sign-contract";
    case n === 5:
      return "/online-checkout/stips";
    case n === 6 && status === 3:
      return "/online-checkout/primary-awaiting";
    case n === 6 && status === 2:
      return "/online-checkout/success-checkout";
    case n === 6:
      return "/online-checkout/review-checkout";
    case n === 7:
      return "/online-checkout/pending-dashboard";
    default:
      return "/online-checkout/view-offer";
  }
};



export const ByzFlexPathForStep = (n: number, status?: number) => {
  console.log(n, status, "ByzFlexPathForStep")
  switch (true) {
    case n === 1:
      return "/online-checkout/byzflex/view-offer";
    case n === 2 && status === 1:
      return "/online-checkout/byzflex/bank-confirmation";
    case n === 2 && status === 2:
      return "/online-checkout/byzflex/bank-verification";
    case n === 2 && status === 3:
      return "/online-checkout/byzflex/bank-select-confirmation";
    case n === 3:
      return "/online-checkout/byzflex/identity-verification";
    case n === 4:
      return "/online-checkout/byzflex/sign-contract";
    case n === 5:
      return "/online-checkout/byzflex/stips";
    case n === 6 && status === 3:
      return "/online-checkout/primary-awaiting";
    case n === 6 && status === 2:
      return "/online-checkout/byzflex/success-checkout";
    case n === 6:
      return "/online-checkout/byzflex/review-checkout";
    case n === 7:
      return "/online-checkout/byzflex/pending-dashboard";
    default:
      return "/online-checkout/byzflex/view-offer";
  }
};

/** Shape of the "last completed step" row returned by LS (`completed_steps` / `complete_step_info`). */
export type CompletedStepRow =
  | { step_number?: string | number | null; status_number?: string | number | null }
  | null
  | undefined;

/**
 * Normalise the backend's last-completed-step row into a usable { step, status }.
 *
 * Resume position is always derived from the last completed step. "No progress" is simply the
 * floor of that rule — step 1 — rather than a special-cased redirect. This collapses the four
 * ways the payload could previously arrive (row present / explicit null / key absent / a value
 * that parses to NaN) into one predictable result, so a deal whose steps were deleted in the
 * Online Checkout Log (ENG-3810) resumes from whatever genuinely remains.
 *
 * Callers must NOT guard this with `!== null` — pass the raw value and always act on the result.
 */
export const normalizeCompletedStep = (
  row: CompletedStepRow
): { step: number; status: number } => {
  const step = parseInt(String(row?.step_number ?? ""), 10);
  const status = parseInt(String(row?.status_number ?? ""), 10);

  // No usable progress. `Number.isFinite` is what catches NaN — `??` does not, which is how
  // `setStepsWithStatus(step ?? 1, ...)` used to write the literal string "NaN" to localStorage
  // and silently kill stored-step navigation for the rest of the session.
  // Returning status 1 (not the parsed status) matters: every call site advances with
  // `status !== 2 ? step : step + 1`, so a floored { 1, 1 } holds on step 1 = view-offer,
  // whereas { 1, 2 } would advance to step 2 and land on the bank screen.
  if (!Number.isFinite(step) || step <= 0) return { step: 1, status: 1 };

  // A step above the routable range (8 = customer_registration, which LS already excludes from
  // the routing queries — ENG-1080) is deliberately passed through rather than clamped: both
  // path resolvers send an out-of-range step to their `default:` case, which is view-offer.
  // Clamping it to 1 would instead let the `step + 1` advance turn it into step 2.
  return { step, status: Number.isFinite(status) && status > 0 ? status : 1 };
};

export const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, ""); // remove non-digits

  if (digits.length !== 10) return value;

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const maskAccountNumber = (
  accountNumber?: string | number,
  visibleDigits: number = 4,
  showXs: boolean = true
): string => {
  if (!accountNumber) return "";

  const acc = accountNumber.toString();
  if (acc.length <= visibleDigits) return acc;

  if (!showXs) {
    return acc.slice(-visibleDigits);
  }
  const maskedLength = acc.length - visibleDigits;
  return "X".repeat(maskedLength) + acc.slice(-visibleDigits);
};

/**
 * ENG-747: Returns true when the bank connect status from LS indicates
 * that bank data has been successfully obtained.
 * Maps to LeadBankConnect PHP constants (actual string values):
 *   STATUS_COMPLETE             = 'Complete'
 *   STATUS_DOWNLOADED_DATA      = 'Downloaded Data'
 *   STATUS_UPDATED_STATEMENTS   = 'Updated Statements'
 *   STATUS_REFRESH_TO_START     = 'Starting Refresh'
 *   STATUS_REFRESH_IN_PROGRESS  = 'Refresh In Progress'
 *   STATUS_PENDING              = 'Pending'
 */
export const isBankConnectComplete = (status?: string): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return (
    s === 'complete' ||
    s === 'downloaded data' ||
    s === 'updated statements' ||
    s === 'starting refresh' ||
    s === 'refresh in progress' ||
    s === 'pending'
  );
};

export const setStepsWithStatus = (step: number, status: number) => {
  console.log("setting step/status in localStorage:", { step, status });
  localStorage.setItem("step", step.toString());
  localStorage.setItem("status", status.toString());
  return true;
};

/**
 * Read the current step/status numbers from localStorage.
 */
export const getStepsWithStatus = () => {
  const step = parseInt(localStorage.getItem("step") || "0", 10) || 0;
  const status = parseInt(localStorage.getItem("status") || "0", 10) || 0;
  return { step, status };
};

/**
 * Given a react-router navigate function and optionally a flag indicating
 * byzflex flow, navigate to the path implied by the stored step/status.  The
 * helper returns `true` if a navigation occurred, `false` otherwise.
 */
export const navigateFromStoredSteps = (
  navigate: (path: string, opts?: any) => void,
  isByzFlex = false
): boolean => {
  const { step, status } = getStepsWithStatus();
  console.log("navigateFromStoredSteps:", { step, status });

  if (!step || !status) return false;
  const pathFn = isByzFlex ? ByzFlexPathForStep : mcaPathForStep;
  const redirect = pathFn((step === 2 && status < 4) || (step === 6 && status !== 3) ? step : status !== 2 ? step : step + 1, status);
  navigate(redirect, { replace: true });
  return true;
};